import React, { useEffect, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

import * as approvalV2Api from '../../../lib/api/approvalV2';
import ApprovalV2HistoryPanel from '../../../components/approval/ApprovalV2HistoryPanel';
import OrderBookApprovalContainer from './OrderBookApprovalContainer';

const DOC_STATUS_LABELS = {
  IN_PROGRESS: '진행중',
  APPROVED: '승인완료',
  REJECTED: '반려',
  RECALLED: '회수',
  CANCELED: '취소',
};

const DOC_STATUS_COLORS = {
  IN_PROGRESS: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  RECALLED: 'default',
  CANCELED: 'default',
};

/**
 * 수주품의서 상세 "결재 내역" 패널 - V2/레거시(V1) 분기(이슈 #9, 2026-10-21).
 *
 * 원인: 결재 상태 자체는 OrderBookInfoContainer 등이 receiveOrderInf에서 바로 읽어 정확히
 * "완료"로 보였지만, 이 패널은 항상 레거시 V1 결재 내역(approvalList, COM_APPR_INF 기반)만
 * 그렸다. bito는 2026-10-20에 결재 V2(COM_APPR_DOC_INF)로 전환했고, 백엔드
 * OrdersService.getOrderMainInf()는 이미 apprDocV2 필드로 V2 문서 헤더를 내려주고 있었는데
 * 프론트 리듀서(modules/projectModule/order.js)가 이를 받아 버리지 않고 있었다 - V2로 상신된
 * 수주품의서는 결국 "결재 내역"에 보여줄 V1 레코드가 없어 항상 빈 패널이었다.
 *
 * apprDocV2(헤더)가 있으면 그 apprDocId로 단계(steps)/액션 이력(actions)을 다시 불러와
 * ApprovalV2HistoryPanel로 보여준다. 없으면(V1으로 상신된 과거 건) 레거시 패널 그대로 유지.
 */
const OrderBookApprovalV2Container = ({ apprDocV2, approvalList }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!apprDocV2?.apprDocId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    approvalV2Api
      .getApprovalV2Document(apprDocV2.apprDocId)
      .then((res) => setDetail(res?.data || null))
      .finally(() => setLoading(false));
  }, [apprDocV2?.apprDocId]);

  if (!apprDocV2?.apprDocId) {
    // V2 문서가 없는 건 - 과거(V1)로 상신된 수주품의서. 기존 레거시 패널을 그대로 쓴다.
    return <OrderBookApprovalContainer approvalList={approvalList} />;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Chip
          size="small"
          label={DOC_STATUS_LABELS[apprDocV2.docStatus] || apprDocV2.docStatus}
          color={DOC_STATUS_COLORS[apprDocV2.docStatus] || 'default'}
        />
        <Typography variant="body2" color="text.secondary">
          상신자: {apprDocV2.submittedByNm || '-'}
        </Typography>
      </Stack>
      {loading && (
        <Typography variant="body2" color="text.secondary">
          불러오는 중...
        </Typography>
      )}
      {!loading && detail && (
        <ApprovalV2HistoryPanel steps={detail.steps || []} actions={detail.actions || []} />
      )}
    </Box>
  );
};

export default OrderBookApprovalV2Container;
